import { getDefaultPipelineHash } from "../Pipelines";
import { IndirectBufferBuilder } from "./IndirectBufferBuilder";
import { BYTES_PER_DRAWCALL } from "./IndirectBufferBuilder";

/* @import { MemoryBlock } from "../../memory/MemoryBlock" */

/** @class MultiDrawRecorder
 * Records a sequence of shapes to draw and records them into MultiDrawCalls.
 *
 * How to use:
 *  - Call start(fragCount) with the number of fragments you expect to record
 *  - Add fragments by calling addFragment(geom, material)
 *  - When done, call finish(), then use getMultiDraws() and getIndirectBuffer() to get the results
 *
 * Note: GPU vertex buffer, index buffer, or material change, a new multiDrawCall is started. More exactly,
 *       a new multiDrawCall is started whenever the pipelineHash changes.
 *       Therefore, adding shapes should be done ordered by pipeline to reduce the number of resulting multiDrawCalls.
 *
 * @typedef {Object} MultiDrawCall
 *    A MultiDrawCall specifies a sequence of drawcalls that can be rendered by a single multiDrawIndexedIndirect call.
 *    It combines all needed data to "replay" the drawCalls (including required pipeline, vertex and index buffers).
 *
 *    @property {GPUBuffer} vb - Vertex buffer
 *    @property {GPUBuffer} ib - Index buffer
 *
 *    @property {number}    indirectOffset - Offset in bytes where the drawcalls are stored in indirectBuffer
 *    @property {number}    drawCount      - Number of drawcalls
 *
 *    // Only needed for pipeline creation. By construction, all shapes in a multiDrawCall must be compatible
 *    // with the same pipeline. So, we only need the first shape to create it.
 *    @property {BufferGeometry} firstGeometry
 *    @property {Material}       firstMaterial
 **/
export class MultiDrawRecorder {

  /** @type {IndirectBufferBuilder} */
  #indirectBufferBuilder = null;

  /** @type {MultiDrawCall[]} */
  #multiDrawCalls = [];

  #device = null;

  /** @type {Object} */
  #curDrawCall = {
    geom: null,
    material: null,
    instanceCount: 0,

    // Note that firstInstance defines where the ObjectUniforms are read from.
    // (may be a single element or the start index of a range if instanceCount > 1)
    firstInstance: 0
  };

  /** @type {MultiDrawCall} */
  #curMultiDraw = {
    vb: null,
    ib: null,
    indirectOffset: 0,
    drawCount: 0,
    firstGeometry: null,
    firstMaterial: null
  };

  // Cached pipelineHash for the MultiDraw currently being filled.
  // We store this outside curMultiDraw, because it's only used during filling.
  /** @type {BigInt} */
  #curPipelineHash = 0n;

  /**
   * Constructor for MultiDrawRecorder.
   *
   * @param {GPUDevice} device - The WebGPU device to use.
   */
  constructor(device) {
    this.#device = device;
  }

  // Returns true if the next shape cannot be rendered in the same multiDraw as the previous one
  #needsNewMultiDraw(geom, pipelineHash) {
    const vbSame = geom.__gpuvb === this.#curMultiDraw.vb;
    const ibSame = geom.__gpuib === this.#curMultiDraw.ib;
    const pipelineSame = this.#curPipelineHash === pipelineHash;
    return !vbSame || !ibSame || !pipelineSame;
  }

  // Returns true if the next geom requires to start a new drawCall (rather than just adding an additional instance to a previous one)
  #needsNewDrawCall(geom, instanceId) {

    // Multiple drawCalls can be combined into a single instanced drawCall if and only if...
    //  1. geometry keeps the same
    //  2. instanceIds form a consecutive range
    //
    const geomChanged = this.#curDrawCall.geom !== geom;
    if (geomChanged) {
      return true;
    }

    // Check if instanceIds are consecutive.
    // Note: This case is usually avoided by sorting the uniforms consistent with the fragments to draw.
    //       It is only needed if we have to skip annoying missing fragments that need to be excluded from drawing.
    const nextInstanceId = this.#curDrawCall.firstInstance + this.#curDrawCall.instanceCount;
    if (instanceId !== nextInstanceId) {
      // We need a new drawCall - otherwise the next instance would use an incorrect instanceId
      return true;
    }
    return false;
  }

  #finishMultiDraw() {
    // Make sure final pending drawCall is included
    this.#finishDrawCall();

    // Don't add if current multiDraw is empty
    if (this.#curMultiDraw.drawCount == 0) {
      return;
    }

    this.#multiDrawCalls.push(this.#curMultiDraw);
    this.#curMultiDraw = null;
    this.#curPipelineHash = 0n;
  }

  #startNewMultiDraw(geom, material) {

    // Make sure the previous multiDraw is finished (if any)
    this.#finishMultiDraw();

    // Get byteOffset where we will start writing drawCalls for the new multiDraw
    const indirectOffset = this.#indirectBufferBuilder.nextIndex() * BYTES_PER_DRAWCALL;

    // Initialize new multiDraw in a way that it is compatible to add the next shape
    this.#curMultiDraw = {
      vb: geom.__gpuvb,
      ib: geom.__gpuib,
      indirectOffset,
      drawCount: 0,
      firstGeometry: geom,
      firstMaterial: material
    };

    // Cache pipleineHash so that we don't recompute it each time again
    this.#curPipelineHash = getDefaultPipelineHash(geom, material);
  }

  #finishDrawCall() {
    // Flush previous drawCall (if any)
    if (this.#curDrawCall.instanceCount > 0) {

      // get drawcall params
      const geom = this.#curDrawCall.geom;
      const indexCount = geom.ib.length;
      const instanceCount = this.#curDrawCall.instanceCount;
      const firstIndex = geom.__gpuibBaseIndex;
      const baseVertex = geom.__gpuvbBaseVertex;
      const firstInstance = this.#curDrawCall.firstInstance;

      // Modified index overrides for edge rendering
      const indexCountEdges = geom.iblines?.length || 0;
      const firstIndexEdges = geom.__gpuibBaseIndex + (geom.__gpuibLinesOffset ?? 0);

      // write them to the buffer
      this.#indirectBufferBuilder.addDrawCall(
        indexCount,
        instanceCount,
        firstIndex,
        baseVertex,
        firstInstance,
        indexCountEdges,
        firstIndexEdges
      );

      // Track number of valid entries in the indirection buffer range
      this.#curMultiDraw.drawCount++;

      // Reset curDrawCall to collect the next one
      this.#curDrawCall.geom = null;
      this.#curDrawCall.material = null;
      this.#curDrawCall.instanceCount = 0;
      this.#curDrawCall.firstInstance = 0;
    }
  }

  #startNewDrawCall(geom, material, instanceId) {
    // Flush previous drawCall (if any)
    this.#finishDrawCall();

    this.#curDrawCall.geom = geom;
    this.#curDrawCall.material = material;
    this.#curDrawCall.instanceCount = 0;
    this.#curDrawCall.firstInstance = instanceId;
  }

  /**
   * Start recording.
   * @param {number} fragCount - The number of fragments to record.
   */
  start(fragCount) {
    this.#indirectBufferBuilder = new IndirectBufferBuilder(this.#device, fragCount);
  }

  /**
   * Add a fragment to be rendered.
   *
   * @param {BufferGeometry} geom - The geometry to render
   * @param {Material} material - The material to use
   * @param {number} instanceId - ID for this instance, used to look up instance-specific data
   */
  addFragment(geom, material, instanceId) {

    const pipelineHash = getDefaultPipelineHash(geom, material);

    // Make sure that the current multiDraw is compatible to allow adding (geom, material).
    // If needed, we may have to start a new multiDrawCall for this first.
    if (this.#needsNewMultiDraw(geom, pipelineHash)) {
      // Start a new multiDrawCall that is compatible to render (geom, material).
      this.#startNewMultiDraw(geom, material);
    }

    // If the geometry changed, we have to start a new drawCall
    if (this.#needsNewDrawCall(geom, instanceId)) {
      this.#startNewDrawCall(geom, material, instanceId);
    }

    // At this point, curDrawCall is already initialized and pointing to geometry and material.
    // So, all that's missing is to add one more instance.
    this.#curDrawCall.instanceCount++;
  }

  /**
   * Finish recording and returns an object providing all data to render the recorded fragments (see below).
   * @param {boolean} enableCulling
   * @param {BufferManagerWebGPU} bufferManager Used when culling is disabled
   *     to upload draw call data to indirect buffers.
   * @returns {Object|null} - The results of the recording, or null if no fragments were recorded.
   */
  finish(enableCulling, bufferManager) {
    this.#finishMultiDraw();

    if (this.#indirectBufferBuilder.empty()) {
      return null;
    }

    // Get resulting GPUBuffers and MemoryBlocks from IndirectBufferBuilder.
    const instanceToDrawCallBuffer = this.#indirectBufferBuilder.createInstanceToDrawCalls();

    let drawCallsBuffer = null;
    let edgeDataBuffer = null;
    let drawCallsBlock = null;
    let edgeDrawCallsBlock = null;

    if (enableCulling) {
      drawCallsBuffer = this.#indirectBufferBuilder.uploadDrawCallsAsBuffer();
      // Only the data that differs from the normal draw calls.
      edgeDataBuffer = this.#indirectBufferBuilder.uploadEdgeData();
    } else {
      drawCallsBlock = this.#indirectBufferBuilder.uploadDrawCalls(bufferManager);
      // Provide a self-contained draw call buffer for edges.
      edgeDrawCallsBlock = this.#indirectBufferBuilder.uploadEdgeDrawCalls(bufferManager);
    }

    return {
      /** @type {MultiDrawCall[]} Sequence of MultiDrawCalls needed to render all recorded fragments */
      multiDraws: this.#multiDrawCalls,

      // These two buffers can used as input for compute shaders.
      /** @type {GPUBuffer|null} Storage buffer containing all draw calls. */
      drawCallsBuffer,
      /** @type {GPUBuffer|null} Pair of indexCount, firstIndex for edge draws */
      edgeDataBuffer,

      // These two memory blocks can be used as indirect buffers to draw from.
      /** @type {MemoryBlock|null} Allocation for indirect draw calls. */
      drawCallsBlock,
      /**
       * @type {MemoryBlock|null} Allocation for indirect edge draw calls.
       *     Note that the drawCalls in this buffer only differ by firstIndex and indexCount.
       *     The rest of the buffer is identical with the main drawCallsBlock.
       */
      edgeDrawCallsBlock,

      /** @type {GPUBuffer} Draw call index for each instance */
      instanceToDrawCallBuffer,
      /** @type {number} Summed number of instances to draw */
      instanceCount: this.#indirectBufferBuilder.getInstanceCount(),
      /** @type {number} Summed number of draw calls over all multidraws */
      drawCallCount: this.#indirectBufferBuilder.getDrawCallCount()
    };
  }
}